<script setup lang="ts">
import PublisherLinks from './PublisherLinks.vue';
import { computed, ref } from 'vue';
import { keptGrants, manifestCardUses, type ModuleManifest, type Permission } from '@vp/shared-types';
import { parsePackage, type ModuleInfo } from '@vp/module-runtime';
import { ask, tell } from '../dialogs';
import { formatDate, t, tm } from '../i18n';
import { settings } from '../platform';
import { countObjectsOf, getRuntime, installModuleFile, modulesState, startCatalogInstall, startFileInstall, uninstallModule } from '../modules';
import { catalogState, installState, isOfficial, loadCatalog, updateFor, type CatalogModule } from '../catalog';
import Icon from './Icon.vue';
import Toggle from './Toggle.vue';
import PermissionList from './PermissionList.vue';
import AccessBridge from './AccessBridge.vue';
import type { Field } from '@vp/shared-types';

/** API 1.6: the module's own settings (manifest `contributes.settings`). */
function settingFields(m: ModuleInfo): Field[] {
  return (m.manifest.contributes.settings ?? []).filter((f) => f.type !== 'panel');
}
function settingValues(m: ModuleInfo): Record<string, unknown> {
  void modulesState.rev;
  return getRuntime()?.settingsOf(m.id) ?? {};
}
/** Show or hide a module's panel button in the top-right bar. */
function setPanelButton(id: string, show: boolean): void {
  const rest = settings.hiddenPanelButtons.filter((x) => x !== id);
  settings.hiddenPanelButtons = show ? rest : [...rest, id];
}
async function setSetting(m: ModuleInfo, key: string, value: unknown): Promise<void> {
  await getRuntime()?.setSettings(m.id, { [key]: value });
}

/**
 * Module management inside Settings (§6.8, §7.6, §7.7, §9.2): searchable cards with logo, name,
 * an on/off switch and a settings button; a card opens the module page with versions, publisher,
 * features and access rights.
 */
const fileInput = ref<HTMLInputElement | null>(null);
const pending = ref<{ file: File; manifest: ModuleManifest; granted: Set<Permission> } | null>(null);
const busy = ref<string | null>(null);
const query = ref('');
const openId = ref<string | null>(null);
/** Installed modules or the catalog (TZ 5.2). */
const tab = ref<'installed' | 'catalog'>('installed');
function showCatalog(): void {
  tab.value = 'catalog';
  openId.value = null;
  if (catalogState.status !== 'loading') void loadCatalog();
}
function catalogName(m: CatalogModule): string {
  return typeof m.name === 'string' ? m.name : tm(m.id, m.name) || m.name['en'] || m.id;
}
function catalogDesc(m: CatalogModule): string {
  return typeof m.description === 'string' ? m.description : tm(m.id, m.description) || m.description['en'] || '';
}
const catalogShown = computed(() => {
  const q = query.value.trim().toLowerCase();
  return catalogState.modules.filter((m) => !q || (catalogName(m) + ' ' + catalogDesc(m) + ' ' + m.id).toLowerCase().includes(q));
});
function installedOf(id: string): ModuleInfo | undefined {
  void modulesState.rev;
  return modulesState.list.find((m) => m.id === id);
}

const list = computed<ModuleInfo[]>(() => {
  void modulesState.rev;
  return [...modulesState.list].sort((a, b) => tm(a.id, a.manifest.name).localeCompare(tm(b.id, b.manifest.name)));
});

/** Everything a module adds, localized: what the search looks through besides its name. */
function features(m: ModuleInfo): { objects: string[]; tools: string[]; commands: string[]; links: string[]; mcp: string[] } {
  const c = m.manifest.contributes;
  return {
    mcp: (c.mcpTools ?? []).map((x) => x.name),
    objects: (c.objectTypes ?? []).map((x) => tm(m.id, x.name)),
    tools: (c.tools ?? []).map((x) => tm(m.id, x.name)),
    commands: (c.commands ?? []).map((x) => tm(m.id, x.name)),
    links: (c.linkTypes ?? []).map((x) => tm(m.id, x.name)),
  };
}

const filtered = computed(() => {
  const q = query.value.trim().toLowerCase();
  if (!q) return list.value;
  return list.value.filter((m) => {
    const f = features(m);
    const hay = [tm(m.id, m.manifest.name), tm(m.id, m.manifest.description), m.id, m.manifest.author, ...f.objects, ...f.tools, ...f.commands, ...f.links];
    return hay.some((s) => s.toLowerCase().includes(q));
  });
});

/** Which feature matched the query (shown on the card so the hit is obvious). */
function matchedFeature(m: ModuleInfo): string | null {
  const q = query.value.trim().toLowerCase();
  if (!q || tm(m.id, m.manifest.name).toLowerCase().includes(q)) return null;
  const f = features(m);
  return [...f.objects, ...f.tools, ...f.commands, ...f.links].find((s) => s.toLowerCase().includes(q)) ?? null;
}

const current = computed(() => (openId.value ? list.value.find((m) => m.id === openId.value) ?? null : null));

function icon(m: ModuleInfo): string | null {
  return getRuntime()?.iconDataUrl(m.id) ?? null;
}

function state(m: ModuleInfo): { cls: string; text: string } {
  switch (m.state) {
    case 'running':
      return { cls: 'ok', text: t('modules.running') };
    case 'isolated':
      return { cls: 'err', text: t('modules.isolated') };
    case 'crashed':
    case 'error':
      return { cls: 'err', text: t('modules.crashed') };
    case 'incompatible':
      return { cls: 'warn', text: t('modules.incompatible') };
    case 'disabled':
      return { cls: 'off', text: t('modules.disabled') };
    default:
      return { cls: 'off', text: m.state };
  }
}

async function run(id: string, fn: () => Promise<void>): Promise<void> {
  busy.value = id;
  try {
    await fn();
  } finally {
    busy.value = null;
  }
}

function setEnabled(m: ModuleInfo, on: boolean): void {
  void run(m.id, () => getRuntime()!.setEnabled(m.id, on));
}

function setPerms(m: ModuleInfo, granted: Permission[]): void {
  void run(m.id, () => getRuntime()!.setGranted(m.id, granted));
}

async function remove(m: ModuleInfo): Promise<void> {
  // API 2.2: a module other modules need stays until they go
  const deps = getRuntime()?.dependentsOf(m.id) ?? [];
  if (deps.length) {
    await tell(t('modules.dependentsBlock', { what: deps.map((id) => { const d = getRuntime()?.get(id); return d ? tm(id, d.manifest.name) : id; }).join(', ') }));
    return;
  }
  if (!(await ask(t('modules.confirmUninstall', { name: tm(m.id, m.manifest.name) }), { okLabel: t('modules.uninstall'), danger: true }))) return;
  await run(m.id, () => uninstallModule(m.id));
  openId.value = null;
}

async function enableDev(): Promise<void> {
  if (await ask(t('settings.devModeWarn'), { title: t('settings.devMode') })) settings.devMode = true;
}

async function pick(e: Event): Promise<void> {
  const f = (e.target as HTMLInputElement).files?.[0];
  (e.target as HTMLInputElement).value = '';
  if (!f) return;
  // a signed file from the showcase: checked and installed like one from the catalog
  if ((await startFileInstall(f)) === 'signed') return;
  if (!settings.devMode) {
    void tell(t('catalog.needsDev'));
    return;
  }
  try {
    const pkg = parsePackage(new Uint8Array(await f.arrayBuffer()));
    const existing = getRuntime()?.get(pkg.manifest.id);
    // request ≠ grant (§9.2): start from previously granted permissions only
    const granted = new Set<Permission>(existing ? keptGrants(pkg.manifest.permissions, existing.granted) : []);
    pending.value = { file: f, manifest: pkg.manifest, granted };
  } catch (err) {
    void tell(t('modules.invalid', { reason: err instanceof Error ? err.message : String(err) }));
  }
}

async function confirmInstall(): Promise<void> {
  const p = pending.value;
  if (!p) return;
  pending.value = null;
  await installModuleFile(p.file, [...p.granted]);
}
</script>

<template>
  <div class="modules">
    <!-- module page -->
    <template v-if="current">
      <button class="back" @click="openId = null"><Icon name="chevronLeft" :size="16" />{{ t('modules.back') }}</button>
      <section class="group hero">
        <img v-if="icon(current)" class="logo big" :src="icon(current)!" alt="" />
        <span v-else class="logo big ph"><Icon name="module" :size="28" /></span>
        <div class="hero-text">
          <h3>{{ tm(current.id, current.manifest.name) }}<span v-if="isOfficial(current)" class="official-badge" data-badge="official">✓ {{ t('catalog.official') }}</span><span v-if="current.manifest.status" class="status-badge" :class="current.manifest.status" :title="t('modules.statusHint.' + current.manifest.status)">{{ t('modules.status.' + current.manifest.status) }}</span></h3>
          <p class="desc">{{ tm(current.id, current.manifest.description) }}</p>
          <span class="state" :class="state(current).cls"><span class="dot"></span>{{ state(current).text }}</span>
        </div>
        <Toggle
          :model-value="current.enabled && current.state !== 'isolated'"
          :label="t('modules.toggleOf', { name: tm(current.id, current.manifest.name) })"
          :disabled="busy === current.id || current.state === 'isolated'"
          @update:model-value="(v) => setEnabled(current!, v)"
        />
      </section>
      <p v-if="current.isolatedReason" class="note err"><Icon name="warning" :size="14" />{{ t('modules.isolatedMsg', { name: tm(current.id, current.manifest.name), reason: current.isolatedReason }) }}</p>
      <p v-else-if="current.error" class="note err"><Icon name="warning" :size="14" />{{ current.error }}</p>

      <h4 class="sec">{{ t('modules.about') }}</h4>
      <section class="group">
        <dl class="facts">
          <div><dt>{{ t('modules.versionLabel') }}</dt><dd>{{ current.version }}</dd></div>
          <div><dt>{{ t('modules.apiLabel') }}</dt><dd>{{ current.manifest.apiVersion }}</dd></div>
          <div v-if="current.manifest.dependencies && Object.keys(current.manifest.dependencies).length"><dt>{{ t('modules.requires') }}</dt><dd>{{ Object.entries(current.manifest.dependencies).map(([id, range]) => (getRuntime()?.get(id) ? tm(id, getRuntime()!.get(id)!.manifest.name) : id) + ' ' + range).join(', ') }}</dd></div>
          <div><dt>{{ t('modules.publisher') }}</dt><dd>{{ current.manifest.author }}</dd></div>
          <div><dt>{{ t('modules.buildDate') }}</dt><dd>{{ formatDate(Date.parse(current.manifest.buildDate)) }}</dd></div>
          <div v-if="current.manifest.license"><dt>{{ t('modules.license') }}</dt><dd>{{ current.manifest.license }}</dd></div>
          <div v-if="current.manifest.source"><dt>{{ t('modules.source') }}</dt><dd class="mono">{{ current.manifest.source }}</dd></div>
          <div v-if="current.manifest.links?.length" class="links-row"><dt>{{ t('modules.links') }}</dt><dd><PublisherLinks :links="current.manifest.links" /></dd></div>
          <div><dt>{{ t('modules.idLabel') }}</dt><dd class="mono">{{ current.id }}</dd></div>
          <div><dt>{{ t('modules.objectsLabel') }}</dt><dd>{{ countObjectsOf(current.id) }}</dd></div>
        </dl>
      </section>

      <h4 class="sec">{{ t('modules.features') }}</h4>
      <section class="group feats">
        <template v-for="[key, items, ico] in [['featObjects', features(current).objects, 'rect'], ['featTools', features(current).tools, 'select'], ['featCommands', features(current).commands, 'sliders'], ['featLinks', features(current).links, 'link'], ['featMcp', features(current).mcp, 'code']] as [string, string[], string][]" :key="key">
          <div v-if="items.length" class="feat-row">
            <span class="feat-label"><Icon :name="ico" :size="14" />{{ t('modules.' + key) }}</span>
            <span class="chips"><span v-for="x in items" :key="x" class="chip">{{ x }}</span></span>
          </div>
        </template>
        <p v-if="!Object.values(features(current)).some((x) => x.length)" class="muted-line">{{ t('modules.noFeatures') }}</p>
      </section>

      <template v-if="current.manifest.contributes.panel">
        <h4 class="sec">{{ t('sidebar.panelSec') }}</h4>
        <section class="group">
          <div class="item">
            <div class="text">
              <span class="label">{{ t('sidebar.showButton') }}</span>
              <span class="hint">{{ t('sidebar.showButtonHint') }}</span>
            </div>
            <Toggle :model-value="!settings.hiddenPanelButtons.includes(current.id)" :label="t('sidebar.showButton')" @update:model-value="(v) => setPanelButton(current!.id, v)" />
          </div>
        </section>
      </template>

      <template v-if="settingFields(current).length">
        <h4 class="sec">{{ t('modules.settingsSec') }}</h4>
        <section class="group">
          <div v-for="f in settingFields(current)" :key="f.key" class="item">
            <div class="text">
              <label class="label" :for="'ms-' + f.key">{{ tm(current.id, f.label) }}</label>
              <span v-if="f.hint" class="hint">{{ tm(current.id, f.hint) }}</span>
            </div>
            <Toggle v-if="f.type === 'toggle'" :model-value="!!settingValues(current)[f.key]" :label="tm(current.id, f.label)" @update:model-value="(v) => setSetting(current!, f.key, v)" />
            <select v-else-if="f.type === 'select'" :id="'ms-' + f.key" class="num-pill wide" :value="settingValues(current)[f.key]" @change="setSetting(current!, f.key, ($event.target as HTMLSelectElement).value)">
              <option v-for="o in f.options" :key="String(o.value)" :value="o.value">{{ tm(current.id, o.label) }}</option>
            </select>
            <input v-else-if="f.type === 'number'" :id="'ms-' + f.key" type="number" class="num-pill" :min="f.min" :max="f.max" :step="f.step ?? 'any'" :value="Number(settingValues(current)[f.key] ?? 0)" @change="setSetting(current!, f.key, +($event.target as HTMLInputElement).value)" />
            <input v-else-if="f.type === 'color'" :id="'ms-' + f.key" type="color" class="swatch-btn" :value="String(settingValues(current)[f.key] ?? '#000000').slice(0, 7)" @change="setSetting(current!, f.key, ($event.target as HTMLInputElement).value)" />
            <input v-else-if="f.type === 'date'" :id="'ms-' + f.key" type="date" class="num-pill wide" :value="String(settingValues(current)[f.key] ?? '')" @change="setSetting(current!, f.key, ($event.target as HTMLInputElement).value)" />
            <input v-else :id="'ms-' + f.key" class="num-pill wide" :type="/token|secret|password|key/i.test(f.key) ? 'password' : 'text'" autocomplete="off" :value="String(settingValues(current)[f.key] ?? '')" @change="setSetting(current!, f.key, ($event.target as HTMLInputElement).value)" />
          </div>
        </section>
      </template>

      <h4 class="sec">{{ t('modules.access') }}</h4>
      <section class="group">
        <p class="sub">{{ t('modules.accessHint') }}</p>
        <PermissionList :requested="current.manifest.permissions" :granted="current.granted" :disabled="busy === current.id" :card-uses="manifestCardUses(current.manifest)" @update="(g) => setPerms(current!, g)" />
        <p v-if="!current.manifest.permissions.length" class="muted-line">{{ t('modules.noPerms') }}</p>
      </section>

      <h4 class="sec">{{ t('modules.manage') }}</h4>
      <section class="group actions-row">
        <button v-if="current.state === 'isolated' || current.state === 'crashed' || current.state === 'error'" class="pill" :disabled="busy === current.id" @click="run(current.id, () => getRuntime()!.restart(current!.id))"><Icon name="refresh" :size="14" />{{ t('modules.restart') }}</button>
        <button v-if="updateFor(current)" class="pill primary" data-act="update" :disabled="installState.busy" @click="startCatalogInstall(current.id, updateFor(current)!.version)"><Icon name="refresh" :size="14" />{{ t('catalog.updateTo', { v: updateFor(current)!.version }) }}</button>
        <button v-if="settings.devMode" class="pill" @click="fileInput?.click()"><Icon name="upload" :size="14" />{{ t('modules.installUpdate') }}</button>
        <button v-for="v in current.previousVersions" :key="v" class="pill" :disabled="busy === current.id" @click="run(current.id, () => getRuntime()!.rollback(current!.id, v))">{{ t('modules.rollback', { v }) }}</button>
        <button class="pill danger" :disabled="busy === current.id" @click="remove(current)"><Icon name="trash" :size="14" />{{ t('modules.uninstall') }}</button>
      </section>
    </template>

    <!-- module list -->
    <template v-else>
      <div class="toolbar">
        <label class="search">
          <Icon name="search" :size="14" />
          <input v-model="query" type="search" :placeholder="t('modules.search')" :aria-label="t('modules.search')" />
        </label>
        <div class="toolbar-row">
          <button class="pill" data-act="install-file" @click="fileInput?.click()"><Icon name="upload" :size="14" />{{ t('catalog.fromFile') }}</button>
          <div class="seg-pills" role="tablist">
            <button role="tab" :aria-selected="tab === 'installed'" :class="{ active: tab === 'installed' }" data-tab="installed" @click="tab = 'installed'">{{ t('catalog.installedTab') }}</button>
            <button role="tab" :aria-selected="tab === 'catalog'" :class="{ active: tab === 'catalog' }" data-tab="catalog" @click="showCatalog">{{ t('catalog.tab') }}</button>
          </div>
        </div>
      </div>

      <template v-if="tab === 'catalog'">
        <p v-if="catalogState.status === 'loading' && !catalogState.modules.length" class="empty-line">{{ t('catalog.loading') }}</p>
        <p v-else-if="catalogState.status === 'offline'" class="note" data-state="catalog-offline"><Icon name="info" :size="14" />{{ t('catalog.offline') }}</p>
        <template v-else>
          <p v-if="catalogState.status === 'stale'" class="note"><Icon name="info" :size="14" />{{ t('catalog.stale') }}</p>
          <p v-if="!catalogShown.length" class="empty-line">{{ catalogState.modules.length ? t('modules.nothingFound') : t('catalog.empty') }}</p>
          <ul v-else class="grid">
            <li v-for="m in catalogShown" :key="m.id" class="card" :data-catalog="m.id">
              <div class="card-main static">
                <img v-if="m.logo" class="logo" :src="m.logo" alt="" />
                <span v-else class="logo ph"><Icon name="module" :size="20" /></span>
                <span class="card-text">
                  <span class="card-name"><span class="name-text">{{ catalogName(m) }}</span><span v-if="m.official" class="official-badge">✓ {{ t('catalog.official') }}</span><span v-if="m.status" class="status-badge" :class="m.status">{{ t('modules.status.' + m.status) }}</span></span>
                  <span class="card-desc">{{ catalogDesc(m) }}</span>
                </span>
              </div>
              <div class="card-side">
                <span class="state off">{{ m.author }} · {{ m.version }}</span>
                <span v-if="installedOf(m.id) && !updateFor(installedOf(m.id)!)" class="installed-mark">{{ t('catalog.installed') }}</span>
                <button v-else class="pill primary small" :disabled="installState.busy" data-act="catalog-install" @click="startCatalogInstall(m.id, m.version)">{{ installedOf(m.id) ? t('catalog.update') : t('catalog.install') }}</button>
              </div>
            </li>
          </ul>
        </template>
      </template>

      <template v-else>
      <p class="note">
        <Icon name="info" :size="14" />
        <span>
          {{ settings.devMode ? t('modules.trustNote') : t('catalog.trustNote') }}
          <button v-if="!settings.devMode" class="link" data-act="enable-dev" @click="enableDev">{{ t('catalog.devLink') }}</button>
        </span>
      </p>

      <div v-if="!list.length" class="empty">
        <span class="logo big ph"><Icon name="module" :size="28" /></span>
        <p>{{ t('modules.none') }}</p>
      </div>
      <p v-else-if="!filtered.length" class="empty-line">{{ t('modules.nothingFound') }}</p>
      <ul v-else class="grid">
        <li v-for="m in filtered" :key="m.id" class="card" :class="{ off: !m.enabled || m.state !== 'running' }">
          <button class="card-main" :aria-label="t('modules.open') + ': ' + tm(m.id, m.manifest.name)" @click="openId = m.id">
            <img v-if="icon(m)" class="logo" :src="icon(m)!" alt="" />
            <span v-else class="logo ph"><Icon name="module" :size="20" /></span>
            <span class="card-text">
              <span class="card-name"><span class="name-text">{{ tm(m.id, m.manifest.name) }}</span><span v-if="isOfficial(m)" class="official-badge">✓ {{ t('catalog.official') }}</span><span v-if="m.manifest.status" class="status-badge" :class="m.manifest.status" :title="t('modules.statusHint.' + m.manifest.status)">{{ t('modules.status.' + m.manifest.status) }}</span></span>
              <span class="card-desc">{{ matchedFeature(m) ?? tm(m.id, m.manifest.description) }}</span>
            </span>
          </button>
          <div class="card-side">
            <span class="state" :class="state(m).cls"><span class="dot"></span>{{ state(m).text }} · {{ m.version }}</span>
            <Toggle
              :model-value="m.enabled && m.state !== 'isolated'"
              :label="t('modules.toggleOf', { name: tm(m.id, m.manifest.name) })"
              :disabled="busy === m.id || m.state === 'isolated'"
              @update:model-value="(v) => setEnabled(m, v)"
            />
            <button class="icon-btn gear" :aria-label="t('modules.settingsOf', { name: tm(m.id, m.manifest.name) })" :title="t('modules.settingsOf', { name: tm(m.id, m.manifest.name) })" @click="openId = m.id">
              <Icon name="settings" :size="16" />
            </button>
          </div>
        </li>
      </ul>
      </template>
    </template>

    <input ref="fileInput" type="file" accept=".vpm,.zip,application/zip" hidden @change="pick" />

    <div v-if="pending" class="dialog-backdrop" @click.self="pending = null">
      <div class="box" role="dialog" aria-modal="true" :aria-label="t('modules.permissionsTitle')">
        <AccessBridge :name="tm(pending.manifest.id, pending.manifest.name)" />
        <div class="split">
          <div class="about">
            <p class="hint">{{ pending.manifest.version }}</p>
            <p class="desc">{{ tm(pending.manifest.id, pending.manifest.description) }}</p>
            <p class="note warn"><Icon name="warning" :size="14" />{{ t('settings.devModeWarn') }}</p>
          </div>
          <div class="perms">
            <PermissionList :requested="pending.manifest.permissions" :granted="[...pending.granted]" :card-uses="manifestCardUses(pending.manifest)" @update="(g) => (pending = { ...pending!, granted: new Set(g) })" />
          </div>
        </div>
        <div class="dialog-actions">
          <button class="pill" @click="pending = null">{{ t('common.cancel') }}</button>
          <button class="pill primary" @click="confirmInstall">{{ t('modules.confirmAccess') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.toolbar {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 12px;
}
.toolbar-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1 1 100%;
  flex-wrap: wrap;
}
.toolbar .pill {
  flex: none;
}
.search {
  flex: 1 1 100%;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  height: 38px;
  padding: 0 14px;
  border-radius: 999px;
  background: var(--panel);
  color: var(--text-3);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
}
.search input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: none;
  font-size: 13px;
}
.pill {
  height: 36px;
  padding: 0 14px;
  font-size: 13px;
}
.pill.primary {
  background: var(--accent);
  border-color: var(--accent);
  color: var(--on-accent);
}
.pill.primary:hover {
  background: var(--accent-2);
  color: var(--on-accent);
}
.pill.danger {
  color: var(--danger);
}
.note {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  margin: 10px 4px 14px;
  font-size: 12px;
  line-height: 1.45;
  color: var(--text-3);
}
.note .link {
  display: inline;
  padding: 0;
  border: none;
  background: none;
  color: var(--accent-text);
  font: inherit;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
}
.note.err {
  color: var(--danger);
}
.note.warn {
  margin: 12px 0;
  padding: 10px 12px;
  border-radius: 12px;
  background: var(--warn-soft);
  color: var(--warn);
}
.note :deep(svg) {
  flex: none;
  margin-top: 1px;
}
.grid {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(290px, 1fr));
  gap: 14px;
}
.card {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background: var(--panel);
  border-radius: 18px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05), 0 4px 16px rgba(15, 23, 42, 0.05);
  overflow: hidden;
  transition: box-shadow 0.15s ease;
}
.card:hover {
  box-shadow: 0 2px 4px rgba(15, 23, 42, 0.06), 0 8px 24px rgba(15, 23, 42, 0.09);
}
.card-main {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 16px 16px 12px;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
}
.card.off .logo,
.card.off .card-name {
  opacity: 0.6;
}
.logo {
  width: 44px;
  height: 44px;
  border-radius: 13px;
  background: var(--hover);
  padding: 9px;
  flex: none;
  object-fit: contain;
}
.logo.big {
  width: 64px;
  height: 64px;
  border-radius: 18px;
  padding: 14px;
}
.logo.ph {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--text-3);
}
.card-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.card-name {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--text);
}
/* a long name is cut with an ellipsis, the status badge next to it stays visible */
.card-name .name-text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* badges go under the name when the card is narrow (the name stays readable) */
.card-name {
  flex-wrap: wrap;
  row-gap: 2px;
}
.card-name .official-badge {
  display: inline-flex;
  align-items: center;
  padding: 0 7px;
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 600;
  line-height: 18px;
  background: var(--ok-soft);
  color: var(--ok);
  vertical-align: middle;
  white-space: nowrap;
}
/* the page behind this row is the same gray as the usual track, so the track is a step darker */
.toolbar-row .seg-pills {
  background: color-mix(in srgb, var(--text) 8%, var(--bg));
}
.toolbar-row .seg-pills button {
  padding: 0 12px;
}
.card-main.static {
  cursor: default;
}
.installed-mark {
  font-size: 12px;
  color: var(--text-3);
  white-space: nowrap;
}
.pill.small {
  height: 28px;
  padding: 0 12px;
  font-size: 12px;
}
.status-badge {
  flex: none;
}
.card-desc {
  font-size: 12px;
  line-height: 1.45;
  color: var(--text-2);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
/* the foot of a card: state on the left, controls on the right, never touching */
.card-side {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 14px;
  padding: 8px 12px 10px 16px;
  border-top: 1px solid var(--border);
  min-height: 48px;
}
.card-side .state {
  margin-right: auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.card-side .icon-btn,
.card-side .pill {
  flex: none;
}
.gear {
  border-radius: 999px;
}
.state {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: var(--text-3);
}
.state .dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--border-strong);
}
.state.ok .dot {
  background: var(--ok);
}
.state.err .dot {
  background: var(--danger);
}
.state.err {
  color: var(--danger);
}
.state.warn .dot {
  background: #f59e0b;
}
.empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 40px 0;
  color: var(--text-3);
}
.empty-line {
  margin: 30px 0;
  text-align: center;
  color: var(--text-3);
}
/* module page */
.back {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin: 0 0 10px;
  padding: 6px 10px 6px 6px;
  border: none;
  border-radius: 999px;
  background: none;
  color: var(--text-2);
  cursor: pointer;
  font-size: 13px;
}
.back:hover {
  background: var(--panel);
  color: var(--text);
}
.group {
  background: var(--panel);
  border-radius: 20px;
  padding: 14px 20px;
  margin-bottom: 14px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05), 0 4px 16px rgba(15, 23, 42, 0.05);
}
.hero {
  display: flex;
  align-items: center;
  gap: 16px;
}
.hero.small {
  gap: 12px;
}
.hero-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
h3 {
  margin: 0;
  font-size: 17px;
  font-weight: 600;
}
.desc {
  margin: 0;
  font-size: 12.5px;
  color: var(--text-2);
  line-height: 1.45;
}
.sec {
  margin: 18px 0 8px 4px;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-3);
}
.facts {
  margin: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px 20px;
}
.facts div {
  min-width: 0;
}
dt {
  font-size: 11px;
  color: var(--text-3);
}
dd {
  margin: 2px 0 0;
  font-size: 13px;
  color: var(--text);
  overflow-wrap: anywhere;
}
.mono {
  font-family: ui-monospace, Consolas, monospace;
  font-size: 12px;
}
.feats {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.feat-row {
  display: flex;
  gap: 12px;
  align-items: baseline;
}
.feat-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  width: 130px;
  flex: none;
  font-size: 12px;
  color: var(--text-3);
}
.status-badge {
  display: inline-block;
  margin-left: 8px;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  vertical-align: middle;
  border: 1px solid transparent;
}
.status-badge.experimental {
  background: color-mix(in srgb, #f59e0b 16%, var(--panel));
  border-color: color-mix(in srgb, #f59e0b 45%, var(--panel));
  color: color-mix(in srgb, #b45309 85%, var(--text));
}
.status-badge.beta {
  background: var(--accent-soft);
  border-color: var(--accent-border);
  color: var(--accent-text);
}
.status-badge.test {
  background: var(--hover);
  border-color: var(--border-strong);
  color: var(--text-2);
}
.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.chip {
  padding: 3px 10px;
  border-radius: 999px;
  background: var(--hover);
  font-size: 12px;
  color: var(--text-2);
}
.sub {
  margin: 0 0 4px;
  font-size: 12px;
  color: var(--text-3);
}
.muted-line {
  margin: 4px 0;
  font-size: 12.5px;
  color: var(--text-3);
}
.item {
  display: flex;
  align-items: center;
  gap: 16px;
  min-height: 54px;
  padding: 8px 0;
  border-top: 1px solid var(--border);
}
.sub + .item {
  border-top: none;
}
.text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.label {
  font-size: 13.5px;
}
.hint {
  font-size: 12px;
  color: var(--text-3);
}
.actions-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.box {
  width: min(820px, calc(100vw - 32px));
  max-height: calc(100vh - 48px);
  overflow: auto;
  padding: 22px 22px 16px;
  background: var(--panel);
  border: 1px solid var(--border);
  border-radius: var(--radius-panel);
  box-shadow: var(--shadow);
}
.box .split {
  display: flex;
  align-items: flex-start;
  gap: 28px;
  margin-top: 16px;
}
.box .about {
  flex: 1 1 260px;
  min-width: 0;
}
.box .about .desc {
  margin-top: 8px;
  font-size: 14px;
  line-height: 1.5;
}
.box .perms {
  flex: 1.15 1 340px;
  min-width: 0;
  padding-left: 22px;
  border-left: 1px solid var(--border);
}
.box .perms .item:first-child {
  border-top: none;
  padding-top: 0;
}
@media (max-width: 720px) {
  .box .split {
    flex-direction: column;
    gap: 16px;
  }
  .box .perms {
    padding-left: 0;
    border-left: none;
  }
}
.dialog-actions {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 20px;
}
.dialog-actions .pill {
  flex: 1;
  max-width: 280px;
  height: 42px;
}
</style>
